import React, { useRef } from "react";
import { cn } from "@/lib/utils";
import { PresignedImage } from "@/components/common/PresignedImage";

interface ProfileImageSelectorProps {
  defaultIcon: React.ReactNode;
  onImageSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  imageClassName?: string;
  overlayContent?: React.ReactNode;
  selectedImage?: string;
  disabled?: boolean;
  /**
   * Tenant of a stored file, sent in `x-tenant-id` with its preview: platform
   * admins in tenant mode (`?tenantId=`, DV-168). Tenant roles leave it out.
   */
  tenantId?: string;
}

const ImageSelector: React.FC<ProfileImageSelectorProps> = ({
  defaultIcon,
  onImageSelect,
  className = "",
  imageClassName = "",
  overlayContent,
  selectedImage,
  disabled = false,
  tenantId,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onImageSelect(event);
  };

  const isBlobUrl = (url: string) => {
    return (
      url.startsWith("blob:") ||
      url.startsWith("data:") ||
      url.startsWith("http")
    );
  };

  const renderImage = () => {
    if (!selectedImage) return defaultIcon;

    if (isBlobUrl(selectedImage)) {
      return (
        <img
          src={selectedImage}
          alt="Selected profile"
          className={cn("h-full w-full object-cover", imageClassName)}
        />
      );
    }

    return (
      <PresignedImage
        fileId={selectedImage}
        tenantId={tenantId}
        alt="Selected profile"
        className={cn("h-full w-full object-cover", imageClassName)}
      />
    );
  };

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center justify-center">{renderImage()}</div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <div
        className={"cursor-pointer"}
        onClick={!disabled ? handleClick : () => {}}
      >
        {overlayContent}
      </div>
    </div>
  );
};

export default ImageSelector;
